/**
 * studentShopViewModel — ce que la « Boutique de gestes » (`<StudentShopPage>`) lit dans le store.
 *
 * Patron A : les produits sont dans les données statiques (`staticData.shop_products`), les pétales
 * et les achats sur le compte (`currentUser.object`), que la réponse d'un achat remplace (réducteur
 * `currentUser`, cas `purchase`). La flèche relit le compte (`getUser`, le tirer-pour-rafraîchir de
 * `MyPetalsTabScreen.js`).
 *
 * Trois arguments viennent de l'écran, chacun lu à part (la mémoïsation tient) : le geste dont la
 * fiche est ouverte, la quête de Tina telle que la réponse de l'achat l'a servie, et si l'accusé est
 * affiché. L'accusé se calcule ici, sur le compte du moment : un titre choisi depuis l'accusé le met à
 * jour (« Ton profil a été mis à jour avec ce titre »), comme l'ancien écran qui relisait le compte.
 */
import { createSelector } from "reselect"

import { type ApiGameSkill, type ApiGameSkillSet } from "../../../adapters/student/shared/questAdapter"
import {
    gestureProducts,
    purchaseCount,
    shopHint,
    toUIShopGesture,
    toUIShopItems,
    toUIShopPurchaseOutcome,
    type ApiShopAccount,
    type ApiShopProduct,
} from "../../../adapters/student/Game/shopAdapter"
import { type UIShopGesture, type UIShopItem, type UIShopPurchaseOutcome } from "../../../ui/types/studentGame"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

export type StudentShopViewModel = {
    /** `null` tant que le compte n'est pas là. */
    petals: number | null
    /** `null` tant que les produits ne sont pas là. */
    items: UIShopItem[] | null
    hint: string
    loading: boolean
    refreshing: boolean
    /** L'achat est parti. */
    purchasing: boolean
    /** Le geste dont la fiche est ouverte, avec son texte long. */
    sheetGesture: UIShopGesture | null
    sheetPurchasedCount: number
    /** Ce que l'accusé annonce, quand il est affiché. */
    outcome: UIShopPurchaseOutcome | null
    /** Le changement de titre demandé depuis l'accusé est parti. */
    choosingNewTitle: boolean
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

const selectStudent = (state: ReduxState): ApiShopAccount | undefined => selectDisplayedStudent(state)
const selectProducts = (state: ReduxState): ApiShopProduct[] | undefined => state?.data?.staticData?.shop_products
const selectSkills = (state: ReduxState): ApiGameSkill[] | undefined => state?.data?.staticData?.skills
const selectUserRequesting = (state: ReduxState): boolean => !!state?.api?.getUser?.request
const selectPurchasing = (state: ReduxState): boolean => !!state?.api?.purchase?.request
const selectChoosingTitle = (state: ReduxState): boolean => !!state?.api?.updateTitleSkillSet?.request
const selectOpenGestureId = (_state: ReduxState, openGestureId: number | null) => openGestureId
const selectGesturesSkillSet = (
    _state: ReduxState,
    _openGestureId: number | null,
    gesturesSkillSet: ApiGameSkillSet | null
) => gesturesSkillSet
const selectPurchased = (
    _state: ReduxState,
    _openGestureId: number | null,
    _gesturesSkillSet: ApiGameSkillSet | null,
    purchased: boolean
) => purchased

export const studentShopViewModel = createSelector(
    [
        selectStudent,
        selectProducts,
        selectSkills,
        selectUserRequesting,
        selectPurchasing,
        selectChoosingTitle,
        selectOpenGestureId,
        selectGesturesSkillSet,
        selectPurchased,
    ],
    (
        account,
        products,
        skills,
        userRequesting,
        purchasing,
        choosingNewTitle,
        openGestureId,
        gesturesSkillSet,
        purchased
    ): StudentShopViewModel => {
        const petals = account != null ? (account.petals ?? 0) : null
        const items = toUIShopItems(products, account?.shop_purchases)
        const product =
            openGestureId != null
                ? gestureProducts(products).find((candidate) => candidate?.id === openGestureId)
                : null
        const sheetGesture = toUIShopGesture(product, true)

        return {
            petals,
            items,
            hint: shopHint(account?.petals),
            loading: petals == null || items == null,
            refreshing: userRequesting,
            purchasing,
            sheetGesture,
            sheetPurchasedCount: sheetGesture != null ? purchaseCount(account?.shop_purchases, sheetGesture.id) : 0,
            outcome:
                purchased && sheetGesture != null
                    ? toUIShopPurchaseOutcome(sheetGesture, account, gesturesSkillSet, skills)
                    : null,
            choosingNewTitle,
        }
    }
)
